import {useMemo, useState, useEffect} from "react";
import { useSelector } from "react-redux";
import { QuoteDataContext } from "./quoteDataContext";
import { useParams } from 'react-router-dom';

import { fetchTags } from "../../tags/data/tagService";
import { fetchSuppliers } from "../../suppliers/data/supplierService";
import { getAllItemLookupTypes } from "../../items/data/itemLookupTypeService";
import { fetchUsers } from "../../users/data/userService";
import { useQuery } from "@tanstack/react-query";
import { fetchOpportunityById, fetchiQuoteByQuoteNumber } from "../../opportunities/data/opportunitiesService";
import { fetchModulesList } from "../../modules/data/moduleService";
import { fetchSalesforceCodes } from "../../salesforceCodes/data/salesforceCodeService";

function QuoteDataProvider({ children }) {

    const [productModules, setProductModules] = useState([]);
    const [basketProcessingModules, setBasketProcessingModules] = useState([]);
    const [salesforceFields, setSalesforceFields] = useState({iQuote: [], Opportunity: []});
    const { id: opportunityId } = useParams();

    // Shared states
    const email = useSelector((state) => state.authentication?.user?.email);

    /*
    * Fetch Item Lookup Types using useQuery
    */
    const {
        data: itemLookupTypesData,
        isLoading: isLoadingItemLookupTypes,
        error: itemLookupTypesError
    } = useQuery({
        queryKey: ["itemLookupTypesWithCustomAttributes"],
        queryFn: getAllItemLookupTypes,
        staleTime: 60000
    });

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tagsFilter"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    // Fetch Suppliers using useQuery
    const {
        data: suppliersData,
        isLoading: isLoadingSuppliers,
        error: suppliersError,
    } = useQuery({
        queryKey: ["itemLookupSuppliersFilter"],
        queryFn: fetchSuppliers,
        staleTime: 60000
    });

    const { 
        data: salesForceCodesData,
        isLoading: isLoadingSalesForceCodes,
        error: salesForceCodesError,
    } = useQuery({
        queryKey: ["salesforceCodes"],
        queryFn: fetchSalesforceCodes,
        staleTime: 60000
    });

    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }));
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["quoteUsersLookupFilter"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });

    const currentUser = useMemo(() => {
        if (!usersLookup?.length || !email) return null;

        return usersLookup.find(f => f.email === email) ?? null;
    }, [usersLookup, email]);



    const getProductModules = async () => {
        var modules = await fetchModulesList();
        modules = modules

        console.log(modules);
        return modules;
    }

    // Fetch product modules using useQuery
    const {
        data: modulesData,
        isLoading: isLoadingModules,
        error: modulesError,
    } = useQuery({
        queryKey: ["productModules"],
        queryFn: getProductModules,
        staleTime: 60000,
    });

    // When usersData is fetched, update our local state.
    useEffect(() => {
        if (modulesData) {
            setProductModules(modulesData.filter(x => x.isSystemModule === false));
            setBasketProcessingModules(modulesData.filter(x => x.isSystemModule === true));
        }    
    }, [modulesData]);

    const getOpportunityObjectAndFields = async () => {
        if (opportunityId === undefined) return undefined;

        return await fetchOpportunityById(opportunityId);
    }

    // Fetch opportunity Fields using useQuery
    const {
        data: opportunityFieldsData,
        isLoading: isLoadingOpportunityFields,
        refetch: refetchOpportunityObjectAndFields
    } = useQuery({
        queryKey: ["opportunityObjectAndFields", opportunityId],
        queryFn: getOpportunityObjectAndFields,
        staleTime: 60000
    });

    // When opportunityFieldData is fetched, update our local state.
    useEffect(() => {
        if (isLoadingOpportunityFields) return;

        console.log('useEffect: opportunityFieldsData')
        if (opportunityFieldsData?.records && opportunityFieldsData?.fieldDefinition) {            
            const opportunityFields = Object.entries(opportunityFieldsData.records).map(([key, value]) => {
                const fieldDefinition = opportunityFieldsData?.fieldDefinition.find(f => f.name === key);
                return ({
                    name: key,
                    label: fieldDefinition?.label ?? key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
                    type: fieldDefinition?.type ?? "string",
                    fieldDefinition: fieldDefinition ?? null
                })
            });

            setSalesforceFields(prev => ({...prev, Opportunity: opportunityFields }));
        }
    }, [opportunityFieldsData]);

    const getiQuoteFields = async () => {
        if (opportunityId === undefined) return [];

        return await fetchiQuoteByQuoteNumber(opportunityId);
    }

    // Fetch iQuote Fields using useQuery
    const {
        data: iQuoteFieldsData,
        isLoading: isLoadingiQuoteFields,
        refetch: refreshiQuoteFields
    } = useQuery({
        queryKey: ["iQuoteFields", opportunityId],
        queryFn: getiQuoteFields,
        staleTime: 60000
    });

    // When iQuoteFieldsData is fetched, update our local state.
    useEffect(() => {
        if (isLoadingiQuoteFields) return;
        
        console.log('useEffect: iQuoteFieldsData')
        if (iQuoteFieldsData?.records && iQuoteFieldsData?.fieldDefinition) {            
            const iQuoteFields = Object.entries(iQuoteFieldsData.records).map(([key, value]) => {
                const fieldDefinition = iQuoteFieldsData?.fieldDefinition.find(f => f.name === key);
                return ({
                    name: key,
                    label: fieldDefinition?.label ?? key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value)
                })
            });            
            
            setSalesforceFields(prev => ({...prev, iQuote: iQuoteFields }));
        }
    }, [iQuoteFieldsData]);

    const refreshSalesforceFields = () => {
        refetchOpportunityObjectAndFields();
        refreshiQuoteFields();
        console.log("✔️ Salesforce fields refreshed")
    }

    return (
        <QuoteDataContext.Provider
            value={{
                tags: tagsData || [],
                itemLookupTypes: itemLookupTypesData || [],
                suppliers: suppliersData || [],
                productModules,
                basketProcessingModules,
                users: usersLookup || [],
                salesforceCodes: salesForceCodesData?.filter(x => !x.disabled) || [],
                currentUser,
                salesforceFields,
                refreshSalesforceFields
            }}
        >
            {children}
        </QuoteDataContext.Provider>
    )
}

export default QuoteDataProvider;